import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  IconButton,
  Stack,
  Container,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Fade,
  Backdrop,
  alpha,
  Divider,
  Skeleton,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Badge,
  Fab
} from '@mui/material';
import {
  Close as CloseIcon,
  Star as StarIcon,
  Restaurant as RestaurantIcon,
  MenuBook as MenuBookIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ShoppingBag as ShoppingBagIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { OrderProvider, useOrder } from '../context/OrderContext';
import MyOrderDrawer from './MyOrderDrawer';
import API_URL from '../config/api';
import { colors, spacing, typography, borderRadius, shadows, transitions } from '../theme/designTokens';

const withCacheBust = (url) => `${url}${url.includes('?') ? '&' : '?'}_=${Date.now()}`;

// Skeleton Loading Components
const HeaderSkeleton = () => (
  <Box
    sx={{
      position: 'relative',
      height: 280,
      borderBottomLeftRadius: 32,
      borderBottomRightRadius: 32,
      overflow: 'hidden',
      mb: 3,
      bgcolor: alpha('#000', 0.05)
    }}
  >
    <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
    <Box
      sx={{
        position: 'absolute',
        bottom: 32,
        left: 24,
        right: 24
      }}
    >
      <Skeleton variant="text" width="70%" height={56} sx={{ mb: 1 }} />
      <Skeleton variant="rounded" width={120} height={32} />
    </Box>
  </Box>
);

const ItemCardSkeleton = ({ isLarge = false }) => (
  <Card
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    }}
  >
    <Stack direction="row" spacing={0}>
      <Skeleton 
        variant="rectangular" 
        width={isLarge ? 120 : 100} 
        height={isLarge ? 120 : 100} 
        animation="wave"
      />
      <CardContent sx={{ flex: 1, p: isLarge ? 2.5 : 2 }}>
        <Skeleton variant="text" width="80%" height={isLarge ? 32 : 24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="40%" height={28} />
      </CardContent>
    </Stack>
  </Card>
);

const CategorySkeleton = () => (
  <Box sx={{ px: 2, mb: 4 }}>
    <Box sx={{ mb: 2 }}>
      <Skeleton variant="text" width="40%" height={40} sx={{ mb: 0.5 }} />
      <Skeleton variant="rectangular" width={60} height={4} sx={{ borderRadius: 2 }} />
    </Box>
    <Stack spacing={2}>
      {[1, 2, 3].map((i) => (
        <ItemCardSkeleton key={i} />
      ))}
    </Stack>
  </Box>
);

// Image Component with Loading and Error States (Optimized)
const ImageWithFallback = React.memo(({ src, alt, width, height, isLarge = false, sx = {} }) => {
  const [imageState, setImageState] = useState(() => src ? 'loading' : 'error');

  useEffect(() => {
    if (!src) {
      setImageState('error');
      return;
    }
    
    setImageState('loading');
    
    const img = new Image();
    let mounted = true;
    
    const handleLoad = () => {
      if (mounted) setImageState('loaded');
    };
    
    const handleError = () => {
      if (mounted) setImageState('error');
    };
    
    img.src = src;
    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);
    
    // Cleanup function
    return () => {
      mounted = false;
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
      // Abort image loading
      img.src = '';
    };
  }, [src]);

  if (imageState === 'loading') {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha('#ea580c', 0.05),
          flexShrink: 0,
          ...sx
        }}
      >
        <CircularProgress size={isLarge ? 32 : 24} sx={{ color: '#ea580c' }} />
      </Box>
    );
  }

  if (imageState === 'error') {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
          flexShrink: 0,
          ...sx
        }}
      >
        <RestaurantIcon sx={{ fontSize: isLarge ? 48 : 36, color: '#ea580c', opacity: 0.5 }} />
      </Box>
    );
  }

  return (
    <CardMedia
      component="img"
      sx={{ 
        width,
        height,
        objectFit: 'cover',
        flexShrink: 0,
        ...sx
      }}
      image={src}
      alt={alt}
      loading="lazy"
    />
  );
});

// Empty State Components
const EmptyState = ({ icon: Icon, title, description, sx = {} }) => (
  <Box
    sx={{
      textAlign: 'center',
      py: spacing[8],
      px: spacing[3],
      ...sx
    }}
  >
    <Box
      sx={{
        display: 'inline-flex',
        p: spacing[3],
        borderRadius: borderRadius.full,
        bgcolor: alpha(colors.primary[600], 0.1),
        mb: spacing[3]
      }}
    >
      <Icon sx={{ fontSize: 48, color: colors.primary[600], opacity: 0.7 }} />
    </Box>
    <Typography 
      variant="h6" 
      fontWeight={typography.fontWeight.bold}
      sx={{ 
        color: colors.secondary[800],
        mb: spacing[1]
      }}
    >
      {title}
    </Typography>
    <Typography 
      variant="body2" 
      sx={{ 
        color: colors.secondary[500],
        lineHeight: typography.lineHeight.relaxed,
        maxWidth: '320px',
        mx: 'auto'
      }}
    >
      {description}
    </Typography>
  </Box>
);

const NoItemsState = () => (
  <EmptyState
    icon={MenuBookIcon}
    title="No Items in This Category"
    description="This category is currently empty. Try exploring other sections of our menu!"
  />
);

// MenuItemCard component - OUTSIDE to prevent recreation on each render
const MenuItemCard = React.memo(({ item, isLarge = false, quantity = 0, onItemClick, onAddToOrder }) => {
  return (
    <Card
      onClick={() => onItemClick(item)}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${item.item_name}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onItemClick(item);
        }
      }}
      sx={{
        cursor: 'pointer',
        borderRadius: borderRadius.lg,
        overflow: 'visible',
        position: 'relative',
        transition: `all ${transitions.duration.medium} ${transitions.timing.spring}`,
        boxShadow: isLarge ? shadows.md : shadows.sm,
        willChange: 'transform',
        '&:hover': {
          transform: isLarge ? 'translateY(-4px)' : 'translateY(-2px)',
          boxShadow: isLarge ? shadows.xl : shadows.lg
        },
        '&:active': {
          transform: 'scale(0.98)'
        }
      }}
    >
      {/* Quantity Badge */}
      {quantity > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            zIndex: 10,
            bgcolor: colors.primary[600],
            color: 'white',
            borderRadius: borderRadius.full,
            minWidth: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.bold,
            boxShadow: shadows.lg,
            border: `2px solid white`,
            animation: 'zoomIn 0.3s ease-out'
          }}
        >
          {quantity}
        </Box>
      )}

      <Stack direction="row" spacing={0} sx={{ height: 120 }}>
        <ImageWithFallback
          src={item.image_url}
          alt={item.item_name}
          width={120}
          height={120}
          isLarge={isLarge}
        />
        <CardContent sx={{ 
          flex: 1, 
          p: spacing[1.5],
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '&:last-child': { pb: spacing[1.5] },
          minWidth: 0,
          overflow: 'hidden'
        }}>
          <Box>
            <Typography 
              variant={isLarge ? "h6" : "subtitle1"} 
              fontWeight={isLarge ? typography.fontWeight.bold : typography.fontWeight.semibold} 
              sx={{ 
                color: colors.secondary[800], 
                mb: isLarge ? spacing[0.5] : '2px', 
                lineHeight: typography.lineHeight.tight,
                pr: spacing[4]
              }}
            >
              {item.item_name}
            </Typography>
            {item.description && (
              <Typography 
                variant={isLarge ? "body2" : "caption"} 
                color="text.secondary" 
                sx={{ 
                  display: '-webkit-box',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: typography.lineHeight.normal,
                  fontSize: isLarge ? typography.fontSize.sm : typography.fontSize.xs,
                  color: colors.secondary[600]
                }}
              >
                {item.description}
              </Typography>
            )}
          </Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={spacing[1]} sx={{ width: '100%', mt: 'auto' }}>
            <Typography 
              variant={isLarge ? "h6" : "subtitle1"} 
              fontWeight={isLarge ? typography.fontWeight.extrabold : typography.fontWeight.bold}
              sx={{ 
                color: colors.primary[600],
                flexShrink: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              ₹{item.price}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => onAddToOrder(item, e)}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              sx={{
                bgcolor: colors.primary[600],
                color: 'white',
                width: 36,
                height: 36,
                boxShadow: shadows.md,
                flexShrink: 0,
                '&:hover': {
                  bgcolor: colors.primary[700],
                  transform: 'scale(1.08)',
                  boxShadow: shadows.lg
                },
                '&:active': {
                  transform: 'scale(0.95)'
                },
                transition: `all ${transitions.duration.medium} ${transitions.timing.spring}`
              }}
            >
              <AddIcon sx={{ fontSize: 20, fontWeight: 'bold' }} />
            </IconButton>
          </Stack>
        </CardContent>
      </Stack>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render)
  return prevProps.item.item_id === nextProps.item.item_id && 
         prevProps.isLarge === nextProps.isLarge &&
         prevProps.quantity === nextProps.quantity &&
         prevProps.onItemClick === nextProps.onItemClick &&
         prevProps.onAddToOrder === nextProps.onAddToOrder;
});

// Main content component that uses order context
const MenuViewerContent = () => {
  const { restaurantId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [menuData, setMenuData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
  
  // Get order context - with safety check
  const orderContext = useOrder();
  const { addItem, getItemQuantity, itemCount = 0, total = 0 } = orderContext || {};

  const handleItemClick = useCallback((item) => {
    setSelectedItem(item);
  }, []);

  const handleCloseItemDetail = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleCategoryClick = useCallback((categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId !== 'all') {
      // Scroll to category
      const element = document.getElementById(`category-${categoryId}`);
      if (element) {
        const headerOffset = 140; // Account for sticky header
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }
  }, []);

  const handleOpenOrderDrawer = useCallback(() => {
    setOrderDrawerOpen(true);
  }, []);

  const handleCloseOrderDrawer = useCallback(() => {
    setOrderDrawerOpen(false);
  }, []);

  const handleAddToOrder = useCallback((item, event) => {
    event?.stopPropagation();
    event?.preventDefault();
    if (addItem) {
      addItem(item);
    }
  }, [addItem]);

  // Filter categories based on search
  const filteredCategories = useMemo(() => {
    if (!menuData?.categories) return [];
    const query = searchQuery.toLowerCase();
    if (!query) return menuData.categories;
    
    return menuData.categories.map(category => ({
      ...category,
      items: (category.items || []).filter(item => 
        item.item_name?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      )
    })).filter(category => category.items.length > 0);
  }, [menuData, searchQuery]);

  const filteredBestsellers = useMemo(() => {
    if (!menuData?.bestsellers) return [];
    const query = searchQuery.toLowerCase();
    if (!query) return menuData.bestsellers;
    
    return menuData.bestsellers.filter(item => 
      item.item_name?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query)
    );
  }, [menuData, searchQuery]);

  useEffect(() => {
    const fetchRestaurantData = async () => {
      try {
        setLoading(true);
        setMenuData(null); // Reset to show skeleton on navigation
        localStorage.removeItem(`menu_${restaurantId}`);

        const response = await fetch(withCacheBust(`${API_URL}/api/restaurants/${restaurantId}`), {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache'
          },
          signal: AbortSignal.timeout(10000) // 10 second timeout
        });
        
        if (!response.ok) {
          throw new Error('Restaurant not found');
        }
        
        const data = await response.json();
        
        if (data.is_active === false) {
          setError({
            disabled: true,
            message: data.disabled_message || 'Thank you for reaching out to QResto. This restaurant menu is currently unavailable.'
          });
          setLoading(false);
          return;
        }
        
        setMenuData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching restaurant:', err);
        if (err.name === 'TimeoutError') {
          setError({ message: 'Connection timeout. Please check your internet and try again.' });
        } else if (err.name === 'AbortError') {
          setError({ message: 'Request timed out. Please try again.' });
        } else {
          setError({ message: err.message || 'Failed to load menu. Please try again.' });
        }
      } finally {
        setLoading(false);
      }
    };

    if (restaurantId) {
      fetchRestaurantData();
    }
  }, [restaurantId]);

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedItem) {
        handleCloseItemDetail();
      }
    };

    if (selectedItem) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedItem, handleCloseItemDetail]);

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(to bottom, #fef9f3 0%, #fff 50%)',
        pb: 4
      }}>
        <Container maxWidth="md" sx={{ py: 0, px: 0 }}>
          <HeaderSkeleton />
          
          {/* Best Sellers Skeleton */}
          <Box sx={{ px: 2, mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton variant="text" width={150} height={40} />
            </Stack>
            <Stack spacing={2}>
              {[1, 2].map((i) => (
                <ItemCardSkeleton key={i} isLarge />
              ))}
            </Stack>
          </Box>

          {/* Categories Skeleton */}
          <CategorySkeleton />
          <CategorySkeleton />
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
        px={3}
      >
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: 500,
            bgcolor: 'white',
            p: 4,
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}
        >
          {error.disabled ? (
            <>
              <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                QResto
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                {error.message}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                For more information, please contact the restaurant directly.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h5" color="error" gutterBottom fontWeight="bold">
                Error
              </Typography>
              <Typography color="text.secondary">
                {error.message || 'Failed to load menu'}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    );
  }

  if (!menuData) {
    return null; // or return loading state
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: `linear-gradient(to bottom, ${colors.background.secondary} 0%, ${colors.background.primary} 50%)`,
      pb: spacing[4]
    }}>
      <Container maxWidth="md" sx={{ py: 0, px: 0 }}>
        {/* Restaurant Header */}
        <Box
          sx={{
            position: 'relative',
            height: menuData.logo_url ? 200 : 160,
            background: menuData.logo_url 
              ? `url(${menuData.logo_url})` 
              : `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[600]} 100%)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderBottomLeftRadius: borderRadius['3xl'],
            borderBottomRightRadius: borderRadius['3xl'],
            overflow: 'hidden',
            mb: spacing[2],
            boxShadow: shadows.xl
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(to bottom, ${colors.overlay.light} 0%, ${colors.overlay.dark} 100%)`
            }}
          />
          
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              p: spacing[3],
              pb: spacing[4]
            }}
          >
            <Typography 
              variant="h3" 
              fontWeight={typography.fontWeight.extrabold}
              color="white" 
              gutterBottom
              sx={{ 
                textShadow: `0 2px 10px ${colors.overlay.light}`,
                letterSpacing: typography.letterSpacing.tight
              }}
            >
              {menuData.restaurant_name}
            </Typography>
            {menuData.cuisine && (
              <Chip 
                label={menuData.cuisine}
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: alpha('#fff', 0.25),
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  fontWeight: typography.fontWeight.semibold,
                  fontSize: typography.fontSize.sm,
                  border: `1px solid ${alpha('#fff', 0.3)}`
                }}
              />
            )}
          </Box>
        </Box>

        {/* Search Bar - Sticky */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            bgcolor: colors.background.secondary,
            pt: spacing[2],
            pb: spacing[2],
            px: spacing[2]
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'white',
              borderRadius: borderRadius.lg,
              border: `2px solid ${searchQuery ? colors.primary[300] : colors.secondary[200]}`,
              px: spacing[2],
              py: spacing[1],
              transition: `border-color ${transitions.duration.fast}`,
              boxShadow: searchQuery ? shadows.md : shadows.sm
            }}
          >
            <SearchIcon sx={{ color: colors.secondary[400], mr: spacing[1] }} />
            <input
              type="text"
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                border: 'none',
                outline: 'none',
                flex: 1,
                fontSize: typography.fontSize.base,
                fontFamily: typography.fontFamily.primary,
                color: colors.secondary[900],
                background: 'transparent'
              }}
            />
            {searchQuery && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ p: spacing[0.5] }}
              >
                <ClearIcon sx={{ fontSize: 20, color: colors.secondary[400] }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Category Navigation - Sticky (Hidden during search) */}
        {!searchQuery && menuData.categories && menuData.categories.length > 0 && (
          <Box
            sx={{
              position: 'sticky',
              top: 72,
              zIndex: 99,
              bgcolor: colors.background.secondary,
              py: spacing[2],
              px: spacing[2],
              mb: spacing[2],
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none'
            }}
          >
            <Stack direction="row" spacing={spacing[1.5]}>
              <Chip
                label="All"
                onClick={() => handleCategoryClick('all')}
                sx={{
                  bgcolor: selectedCategory === 'all' ? colors.primary[600] : 'white',
                  color: selectedCategory === 'all' ? 'white' : colors.secondary[700],
                  fontWeight: typography.fontWeight.semibold,
                  fontSize: typography.fontSize.sm,
                  px: spacing[2],
                  py: spacing[1],
                  height: 'auto',
                  borderRadius: borderRadius.lg,
                  border: `2px solid ${selectedCategory === 'all' ? colors.primary[600] : colors.secondary[200]}`,
                  cursor: 'pointer',
                  boxShadow: selectedCategory === 'all' ? shadows.sm : 'none',
                  transition: `all ${transitions.duration.fast}`,
                  '&:hover': {
                    bgcolor: selectedCategory === 'all' ? colors.primary[700] : colors.secondary[50],
                    transform: 'translateY(-2px)',
                    boxShadow: shadows.md
                  }
                }}
              />
              {menuData.categories.filter(cat => cat.items && cat.items.length > 0).map((category) => (
                <Chip
                  key={category.category_id}
                  label={category.category_name}
                  onClick={() => handleCategoryClick(category.category_id)}
                  sx={{
                    bgcolor: selectedCategory === category.category_id ? colors.primary[600] : 'white',
                    color: selectedCategory === category.category_id ? 'white' : colors.secondary[700],
                    fontWeight: typography.fontWeight.semibold,
                    fontSize: typography.fontSize.sm,
                    px: spacing[2],
                    py: spacing[1],
                    height: 'auto',
                    borderRadius: borderRadius.lg,
                    border: `2px solid ${selectedCategory === category.category_id ? colors.primary[600] : colors.secondary[200]}`,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    boxShadow: selectedCategory === category.category_id ? shadows.sm : 'none',
                    transition: `all ${transitions.duration.fast}`,
                    '&:hover': {
                      bgcolor: selectedCategory === category.category_id ? colors.primary[700] : colors.secondary[50],
                      transform: 'translateY(-2px)',
                      boxShadow: shadows.md
                    }
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {filteredBestsellers.length > 0 ? (
          <Box sx={{ px: spacing[2], mb: spacing[4], mt: spacing[2] }}>
            <Stack direction="row" alignItems="center" spacing={spacing[1.5]} mb={spacing[2.5]}>
              <StarIcon sx={{ color: colors.accent[600], fontSize: 26 }} />
              <Typography 
                variant="h5" 
                fontWeight={typography.fontWeight.bold}
                sx={{ 
                  background: `linear-gradient(135deg, ${colors.accent[600]} 0%, ${colors.primary[600]} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Best Sellers
              </Typography>
            </Stack>
            <Stack spacing={spacing[2]}>
              {filteredBestsellers.slice(0, 3).map((item) => (
                <MenuItemCard 
                  key={item.item_id} 
                  item={item} 
                  quantity={getItemQuantity ? getItemQuantity(item.item_id) : 0}
                  onItemClick={handleItemClick}
                  onAddToOrder={handleAddToOrder}
                />
              ))}
            </Stack>
          </Box>
        ) : null}

        {filteredCategories.map((category, categoryIndex) => (
          <Fade in key={category.category_id} timeout={800} style={{ transitionDelay: `${categoryIndex * 100}ms` }}>
            <Box id={`category-${category.category_id}`} sx={{ px: spacing[2], mb: spacing[4] }}>
              <Box sx={{ mb: spacing[2] }}>
                <Typography 
                  variant="h5" 
                  fontWeight={typography.fontWeight.bold}
                  sx={{ 
                    color: colors.secondary[800],
                    mb: spacing[0.5]
                  }}
                >
                  {category.category_name}
                </Typography>
                <Divider sx={{ 
                  borderColor: category.color || colors.primary[600],
                  borderWidth: 2,
                  width: 60,
                  borderRadius: borderRadius.base
                }} />
              </Box>

              <Stack spacing={spacing[2]}>
                {category.items && category.items.length > 0 ? (
                  category.items.map((item) => (
                    <MenuItemCard 
                      key={item.item_id} 
                      item={item} 
                      quantity={getItemQuantity ? getItemQuantity(item.item_id) : 0}
                      onItemClick={handleItemClick}
                      onAddToOrder={handleAddToOrder}
                    />
                  ))
                ) : (
                  <NoItemsState />
                )}
              </Stack>
            </Box>
          </Fade>
        ))}

        {/* No results message */}
        {searchQuery && filteredCategories.length === 0 && filteredBestsellers.length === 0 && (
          <Box sx={{ textAlign: 'center', py: spacing[8], px: spacing[3] }}>
            <SearchIcon sx={{ fontSize: 64, color: colors.secondary[300], mb: spacing[2] }} />
            <Typography 
              variant="h6" 
              fontWeight={typography.fontWeight.bold}
              sx={{ color: colors.secondary[800], mb: spacing[1] }}
            >
              No items found
            </Typography>
            <Typography variant="body2" sx={{ color: colors.secondary[500] }}>
              Try searching with different keywords
            </Typography>
          </Box>
        )}

        <Box sx={{ textAlign: 'center', py: spacing[4], px: spacing[2] }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.secondary[400],
              fontWeight: typography.fontWeight.medium,
              letterSpacing: typography.letterSpacing.wide
            }}
          >
            Powered by <Box component="span" sx={{ 
              fontWeight: typography.fontWeight.bold,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>QResto</Box>
          </Typography>
        </Box>
      </Container>

      {/* Bottom Sheet for Mobile, Centered Modal for Desktop */}
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={!!selectedItem}
          onClose={handleCloseItemDetail}
          onOpen={() => {}}
          disableSwipeToOpen
          sx={{
            '& .MuiDrawer-paper': {
              borderTopLeftRadius: borderRadius['2xl'],
              borderTopRightRadius: borderRadius['2xl'],
              maxHeight: '90vh'
            }
          }}
        >
          {selectedItem && (
            <Box
              role="dialog"
              aria-modal="true"
              aria-labelledby="item-detail-title"
              aria-describedby="item-detail-description"
            >
              {selectedItem.image_url ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 240,
                    backgroundImage: `url(${selectedItem.image_url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {/* Drag Handle overlaid on image */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: spacing[1.5],
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 2
                    }}
                  >
                    <Box
                      sx={{
                        width: '40px',
                        height: '4px',
                        borderRadius: borderRadius.full,
                        bgcolor: alpha('#fff', 0.9),
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: colors.overlay.light
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 240,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${colors.primary[200]} 0%, ${colors.primary[300]} 100%)`
                  }}
                >
                  {/* Drag Handle overlaid on placeholder */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: spacing[1.5],
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 2
                    }}
                  >
                    <Box
                      sx={{
                        width: '40px',
                        height: '4px',
                        borderRadius: borderRadius.full,
                        bgcolor: alpha('#fff', 0.9),
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </Box>
                  <RestaurantIcon sx={{ fontSize: 80, color: colors.primary[600], opacity: 0.5 }} />
                </Box>
              )}

              <Box sx={{ p: spacing[3], pb: `calc(${spacing[3]} + env(safe-area-inset-bottom))` }}>
                <Typography 
                  id="item-detail-title"
                  variant="h5" 
                  fontWeight={typography.fontWeight.extrabold}
                  gutterBottom 
                  sx={{ color: colors.secondary[800], pr: spacing[5] }}
                >
                  {selectedItem.item_name}
                </Typography>

                {selectedItem.description && (
                  <Typography 
                    id="item-detail-description"
                    variant="body1" 
                    color="text.secondary" 
                    sx={{ 
                      mb: spacing[3],
                      lineHeight: typography.lineHeight.relaxed,
                      color: colors.secondary[600]
                    }}
                  >
                    {selectedItem.description}
                  </Typography>
                )}

                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={spacing[2]}>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: spacing[3],
                      py: spacing[1.5],
                      borderRadius: borderRadius.md,
                      bgcolor: alpha(colors.primary[600], 0.1)
                    }}
                  >
                    <Typography 
                      variant="h4" 
                      fontWeight={typography.fontWeight.extrabold}
                      sx={{ 
                        color: colors.primary[600]
                      }}
                    >
                      ₹{selectedItem.price}
                    </Typography>
                  </Box>
                  
                  <IconButton
                    onClick={(e) => {
                      handleAddToOrder(selectedItem, e);
                      handleCloseItemDetail();
                    }}
                    sx={{
                      bgcolor: colors.primary[600],
                      color: 'white',
                      width: 56,
                      height: 56,
                      boxShadow: shadows.lg,
                      '&:hover': {
                        bgcolor: colors.primary[700],
                        transform: 'scale(1.08)',
                        boxShadow: shadows.xl
                      },
                      '&:active': {
                        transform: 'scale(0.95)'
                      },
                      transition: `all ${transitions.duration.medium} ${transitions.timing.spring}`
                    }}
                  >
                    <AddIcon sx={{ fontSize: 28, fontWeight: 'bold' }} />
                  </IconButton>
                </Stack>
              </Box>
            </Box>
          )}
        </SwipeableDrawer>
      ) : (
        <>
          <Backdrop
            open={!!selectedItem}
            onClick={handleCloseItemDetail}
            sx={{
              zIndex: 1300,
              backdropFilter: 'blur(8px)',
              backgroundColor: colors.overlay.dark
            }}
          />
          
          <Fade in={!!selectedItem} timeout={400} unmountOnExit>
            {selectedItem ? (
              <Box
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="item-detail-title"
                aria-describedby="item-detail-description"
                sx={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 'calc(100% - 32px)',
                  maxWidth: 500,
                  maxHeight: '85vh',
                  bgcolor: 'background.paper',
                  borderRadius: borderRadius.xl,
                  boxShadow: shadows['2xl'],
                  zIndex: 1301,
                  overflow: 'auto'
                }}
              >
                {selectedItem.image_url ? (
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: 250,
                      backgroundImage: `url(${selectedItem.image_url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: colors.overlay.light
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: 250,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${colors.primary[200]} 0%, ${colors.primary[300]} 100%)`
                    }}
                  >
                    <RestaurantIcon sx={{ fontSize: 80, color: colors.primary[600], opacity: 0.5 }} />
                  </Box>
                )}

                <IconButton
                  onClick={handleCloseItemDetail}
                  aria-label="Close item details"
                  sx={{
                    position: 'absolute',
                    top: spacing[1.5],
                    right: spacing[1.5],
                    minWidth: 44,
                    minHeight: 44,
                    bgcolor: alpha('#fff', 0.9),
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      bgcolor: 'white'
                    },
                    '&:active': {
                      transform: 'scale(0.95)'
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>

                <Box sx={{ p: spacing[3] }}>
                  <Typography 
                    id="item-detail-title"
                    variant="h4" 
                    fontWeight={typography.fontWeight.extrabold}
                    gutterBottom 
                    sx={{ color: colors.secondary[800], pr: spacing[5] }}
                  >
                    {selectedItem.item_name}
                  </Typography>

                  {selectedItem.description && (
                    <Typography 
                      id="item-detail-description"
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ 
                        mb: spacing[3],
                        lineHeight: typography.lineHeight.relaxed,
                        color: colors.secondary[600]
                      }}
                    >
                      {selectedItem.description}
                    </Typography>
                  )}

                  <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={spacing[2]}>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: spacing[3],
                        py: spacing[1.5],
                        borderRadius: borderRadius.md,
                        bgcolor: alpha(colors.primary[600], 0.1)
                      }}
                    >
                      <Typography 
                        variant="h4" 
                        fontWeight={typography.fontWeight.extrabold}
                        sx={{ 
                          color: colors.primary[600]
                        }}
                      >
                        ₹{selectedItem.price}
                      </Typography>
                    </Box>
                    
                    <IconButton
                      onClick={(e) => {
                        handleAddToOrder(selectedItem, e);
                        handleCloseItemDetail();
                      }}
                      sx={{
                        bgcolor: colors.primary[600],
                        color: 'white',
                        width: 56,
                        height: 56,
                        boxShadow: shadows.lg,
                        '&:hover': {
                          bgcolor: colors.primary[700],
                          transform: 'scale(1.08)',
                          boxShadow: shadows.xl
                        },
                        '&:active': {
                          transform: 'scale(0.95)'
                        },
                        transition: `all ${transitions.duration.medium} ${transitions.timing.spring}`
                      }}
                    >
                      <AddIcon sx={{ fontSize: 28, fontWeight: 'bold' }} />
                    </IconButton>
                  </Stack>
                </Box>
              </Box>
            ) : (
              <Box /> // Placeholder for Fade
            )}
          </Fade>
        </>
      )}

      {/* My Order Drawer */}
      <MyOrderDrawer
        open={orderDrawerOpen}
        onClose={handleCloseOrderDrawer}
        onOpen={handleOpenOrderDrawer}
        restaurantName={menuData?.restaurant_name}
      />

      {/* Floating Order Button */}
      {itemCount > 0 && (
        <Fab
          onClick={handleOpenOrderDrawer}
          sx={{
            position: 'fixed',
            bottom: spacing[3],
            right: spacing[3],
            bgcolor: colors.primary[600],
            color: 'white',
            width: isMobile ? '85%' : 'auto',
            maxWidth: isMobile ? 'calc(100% - 48px)' : 'auto',
            height: 56,
            borderRadius: borderRadius.full,
            boxShadow: shadows['2xl'],
            left: isMobile ? '50%' : 'auto',
            transform: isMobile ? 'translateX(-50%)' : 'none',
            px: spacing[3],
            animation: 'slideUpFadeIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '@keyframes slideUpFadeIn': {
              '0%': {
                opacity: 0,
                transform: isMobile ? 'translateX(-50%) translateY(30px)' : 'translateY(30px)'
              },
              '100%': {
                opacity: 1,
                transform: isMobile ? 'translateX(-50%) translateY(0)' : 'translateY(0)'
              }
            },
            '&:hover': {
              bgcolor: colors.primary[700],
              transform: isMobile ? 'translateX(-50%) scale(1.05)' : 'scale(1.05)'
            },
            '&:active': {
              transform: isMobile ? 'translateX(-50%) scale(0.98)' : 'scale(0.98)'
            },
            transition: `all ${transitions.duration.medium} ${transitions.timing.spring}`,
            zIndex: 1000
          }}
          variant="extended"
        >
          <Badge
            badgeContent={itemCount}
            sx={{
              mr: spacing[1.5],
              '& .MuiBadge-badge': {
                bgcolor: 'white',
                color: colors.primary[600],
                fontWeight: typography.fontWeight.bold,
                fontSize: typography.fontSize.xs
              }
            }}
          >
            <ShoppingBagIcon sx={{ fontSize: 24 }} />
          </Badge>
          <Typography
            variant="body1"
            fontWeight={typography.fontWeight.bold}
            sx={{ mr: 'auto' }}
          >
            My Order
          </Typography>
          <Typography
            variant="body1"
            fontWeight={typography.fontWeight.extrabold}
          >
            ₹{total}
          </Typography>
        </Fab>
      )}
    </Box>
  );
};

// Wrapper component with OrderProvider
const MenuViewer = () => {
  const { restaurantId } = useParams();
  
  return (
    <OrderProvider restaurantId={restaurantId}>
      <MenuViewerContent />
    </OrderProvider>
  );
};

export default MenuViewer;
